// store/slices/uiSlice.ts
import { StateCreator } from "zustand";

export interface ToastNotification {
  id: string;
  message: string;
  type: "info" | "success" | "warning" | "error";
  duration?: number;
}

export interface UIState {
  activeModal: {
    name: string;
    props?: any;
  } | null;
  isExporting: boolean;
  exportProgress: number;
  viewMode: "grid" | "fullsize";
  currentImageIndex: number;
  editMode: boolean;
  currentView: "home" | "grid" | "library" | "posts" | "stories" | "reporting" | "ideas" | "briefs" | "ads" | "finance";
  isLibraryLoading: boolean;
  libraryProgress: number;
  notesRefreshKey: number;
  toasts: ToastNotification[];
}

export interface UISlice {
  ui: UIState;
  setActiveModal: (modal: string | null, props?: any) => void;
  setExportProgress: (progress: number) => void;
  setExporting: (isExporting: boolean) => void;
  setViewMode: (mode: "grid" | "fullsize") => void;
  setCurrentView: (view: "home" | "grid" | "library" | "posts" | "stories" | "reporting" | "ideas" | "briefs" | "ads" | "finance") => void;
  setCurrentImageIndex: (index: number) => void;
  setEditMode: (editMode: boolean) => void;
  setLibraryLoading: (isLoading: boolean) => void;
  setLibraryProgress: (progress: number) => void;
  triggerNotesRefresh: () => void;
  addToast: (toast: Omit<ToastNotification, "id">) => void;
  removeToast: (id: string) => void;
}

export const createUISlice: StateCreator<UISlice> = (set) => ({
  ui: {
    activeModal: null,
    isExporting: false,
    exportProgress: 0,
    viewMode: "grid",
    currentImageIndex: 0,
    editMode: false,
    currentView: "grid",
    isLibraryLoading: false,
    libraryProgress: 0,
    notesRefreshKey: 0,
    toasts: [],
  },

  setActiveModal: (modalName, props) =>
    set((state) => ({
      ui: {
        ...state.ui,
        activeModal: modalName ? { name: modalName, props } : null,
      },
    })),

  setExportProgress: (exportProgress) =>
    set((state) => ({
      ui: { ...state.ui, exportProgress },
    })),

  setExporting: (isExporting) =>
    set((state) => ({
      ui: { ...state.ui, isExporting },
    })),

  setViewMode: (viewMode) =>
    set((state) => ({
      ui: { ...state.ui, viewMode },
    })),

  setCurrentView: (currentView) =>
    set((state) => ({
      ui: { ...state.ui, currentView },
    })),

  setCurrentImageIndex: (currentImageIndex) =>
    set((state) => ({
      ui: { ...state.ui, currentImageIndex },
    })),

  setEditMode: (editMode) =>
    set((state) => ({
      ui: { ...state.ui, editMode },
    })),

  setLibraryLoading: (isLibraryLoading) =>
    set((state) => ({
      ui: { ...state.ui, isLibraryLoading },
    })),

  setLibraryProgress: (libraryProgress) =>
    set((state) => ({
      ui: { ...state.ui, libraryProgress },
    })),

  triggerNotesRefresh: () =>
    set((state) => ({
      ui: { ...state.ui, notesRefreshKey: Date.now() },
    })),

  addToast: (toast) =>
    set((state) => ({
      ui: {
        ...state.ui,
        toasts: [
          ...state.ui.toasts,
          { ...toast, id: `toast-${Date.now()}-${Math.random().toString(36).slice(2)}` },
        ],
      },
    })),

  removeToast: (id) =>
    set((state) => ({
      ui: {
        ...state.ui,
        toasts: state.ui.toasts.filter((t) => t.id !== id),
      },
    })),
});
